import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';

// Element types
export const ELEMENT_TYPES = {
  RECTANGLE: 'rectangle',
  DIAMOND: 'diamond',
  CIRCLE: 'circle',
  LINE: 'line',
  ARROW: 'arrow',
  TEXT: 'text',
  FREEHAND: 'freehand',
  IMAGE: 'image',
  ICON: 'icon',
  CONNECTOR: 'connector',
};

// Tool types
export const TOOL_TYPES = {
  SELECT: 'select',
  HAND: 'hand',
  PAN: 'pan',
  RECTANGLE: 'rectangle',
  DIAMOND: 'diamond',
  CIRCLE: 'circle',
  LINE: 'line',
  ARROW: 'arrow',
  TEXT: 'text',
  FREEHAND: 'freehand',
  ERASER: 'eraser',
  CONNECTOR: 'connector',
  IMAGE: 'image',
};

// Default styles
const DEFAULT_STYLES = {
  strokeColor: '#1e1e1e',
  fillColor: 'transparent',
  strokeWidth: 2,
  fontSize: 16,
  fontFamily: 'Inter, sans-serif',
  opacity: 1,
  roughness: 0,
};

// Create element factory
const createElement = (type, props = {}) => ({
  id: uuidv4(),
  type,
  x: 0,
  y: 0,
  width: 100,
  height: 100,
  rotation: 0,
  locked: false,
  visible: true,
  layerIndex: 0,
  groupId: null,
  ...DEFAULT_STYLES,
  ...props,
  createdAt: Date.now(),
  updatedAt: Date.now(),
});

// Main store
const useDrawStore = create((set, get) => ({
  // Canvas state
  elements: [],
  selectedIds: [],
  copiedElements: [],
  
  // Tool state
  activeTool: TOOL_TYPES.SELECT,
  activeStyles: { ...DEFAULT_STYLES },
  
  // View state
  zoom: 1,
  panX: 0,
  panY: 0,
  showGrid: true,
  snapToGrid: true,
  gridSize: 20,
  
  // History for undo/redo
  history: [],
  historyIndex: -1,
  maxHistory: 50,
  
  // Collaboration
  collaborators: [],
  sessionId: null,
  isConnected: false,
  
  // UI state
  isDarkMode: false,
  showIconLibrary: false,
  showProperties: true,
  showLayers: false,
  
  // Document info
  documentId: null,
  documentName: 'Sans titre',
  lastSaved: null,
  isDirty: false,

  // ============ ELEMENT ACTIONS ============
  
  addElement: (type, props = {}) => {
    const element = createElement(type, {
      ...get().activeStyles,
      ...props,
      layerIndex: get().elements.length,
    });
    
    set(state => {
      const newElements = [...state.elements, element];
      return {
        elements: newElements,
        selectedIds: [element.id],
        isDirty: true,
      };
    });
    
    get().saveToHistory();
    return element;
  },
  
  updateElement: (id, updates) => {
    set(state => ({
      elements: state.elements.map(el =>
        el.id === id ? { ...el, ...updates, updatedAt: Date.now() } : el
      ),
      isDirty: true,
    }));
  },
  
  updateElements: (ids, updates) => {
    set(state => ({
      elements: state.elements.map(el =>
        ids.includes(el.id) ? { ...el, ...updates, updatedAt: Date.now() } : el
      ),
      isDirty: true,
    }));
    get().saveToHistory();
  },
  
  deleteElements: (ids) => {
    set(state => ({
      elements: state.elements.filter(el => !ids.includes(el.id)),
      selectedIds: state.selectedIds.filter(id => !ids.includes(id)),
      isDirty: true,
    }));
    get().saveToHistory();
  },
  
  deleteSelected: () => {
    const { selectedIds } = get();
    if (selectedIds.length > 0) {
      get().deleteElements(selectedIds);
    }
  },
  
  // Select all elements
  selectAll: () => {
    const { elements } = get();
    const allIds = elements
      .filter(el => el.type !== ELEMENT_TYPES.CONNECTOR) // Exclude connectors from selection
      .map(el => el.id);
    set({ selectedIds: allIds });
  },
  
  // Clear all canvas elements
  clearCanvas: () => {
    set({
      elements: [],
      selectedIds: [],
      isDirty: false,
      documentName: 'Sans titre',
    });
    // Reset history
    set({
      history: ['[]'],
      historyIndex: 0,
    });
  },
  
  duplicateElements: (ids) => {
    const { elements } = get();
    const toDuplicate = elements.filter(el => ids.includes(el.id));
    const duplicated = toDuplicate.map(el => ({
      ...el,
      id: uuidv4(),
      x: el.x + 20,
      y: el.y + 20,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    }));
    
    set(state => ({
      elements: [...state.elements, ...duplicated],
      selectedIds: duplicated.map(el => el.id),
      isDirty: true,
    }));
    get().saveToHistory();
  },
  
  // ============ SELECTION ============
  
  setSelectedIds: (ids) => set({ selectedIds: ids }),
  
  addToSelection: (id) => set(state => ({
    selectedIds: state.selectedIds.includes(id)
      ? state.selectedIds
      : [...state.selectedIds, id]
  })),
  
  removeFromSelection: (id) => set(state => ({
    selectedIds: state.selectedIds.filter(i => i !== id)
  })),
  
  selectAll: () => set(state => ({
    selectedIds: state.elements.filter(el => !el.locked).map(el => el.id)
  })),
  
  clearSelection: () => set({ selectedIds: [] }),
  
  // ============ CLIPBOARD ============
  
  copy: () => {
    const { elements, selectedIds } = get();
    const toCopy = elements.filter(el => selectedIds.includes(el.id));
    set({ copiedElements: toCopy });
  },
  
  paste: () => {
    const { copiedElements } = get();
    if (copiedElements.length === 0) return;
    
    const pasted = copiedElements.map(el => ({
      ...el,
      id: uuidv4(),
      x: el.x + 20,
      y: el.y + 20,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    }));
    
    set(state => ({
      elements: [...state.elements, ...pasted],
      selectedIds: pasted.map(el => el.id),
      isDirty: true,
    }));
    get().saveToHistory();
  },
  
  cut: () => {
    get().copy();
    get().deleteSelected();
  },
  
  // ============ GROUPING ============
  
  groupSelected: () => {
    const { selectedIds, elements } = get();
    if (selectedIds.length < 2) return;
    
    const groupId = uuidv4();
    set(state => ({
      elements: state.elements.map(el =>
        selectedIds.includes(el.id) ? { ...el, groupId } : el
      ),
      isDirty: true,
    }));
    get().saveToHistory();
  },
  
  ungroupSelected: () => {
    const { selectedIds } = get();
    set(state => ({
      elements: state.elements.map(el =>
        selectedIds.includes(el.id) ? { ...el, groupId: null } : el
      ),
      isDirty: true,
    }));
    get().saveToHistory();
  },
  
  // ============ LOCKING ============
  
  lockSelected: () => {
    const { selectedIds } = get();
    get().updateElements(selectedIds, { locked: true });
    set({ selectedIds: [] });
  },
  
  unlockElement: (id) => {
    get().updateElement(id, { locked: false });
  },
  
  // ============ LAYERS ============
  
  bringToFront: () => {
    const { selectedIds, elements } = get();
    if (selectedIds.length === 0) return;
    
    const maxIndex = Math.max(...elements.map(el => el.layerIndex));
    set(state => ({
      elements: state.elements.map(el =>
        selectedIds.includes(el.id)
          ? { ...el, layerIndex: maxIndex + 1 }
          : el
      ),
      isDirty: true,
    }));
    get().saveToHistory();
  },
  
  sendToBack: () => {
    const { selectedIds, elements } = get();
    if (selectedIds.length === 0) return;
    
    const minIndex = Math.min(...elements.map(el => el.layerIndex));
    set(state => ({
      elements: state.elements.map(el =>
        selectedIds.includes(el.id)
          ? { ...el, layerIndex: minIndex - 1 }
          : el
      ),
      isDirty: true,
    }));
    get().saveToHistory();
  },
  
  // ============ HISTORY ============
  
  saveToHistory: () => {
    const { elements, history, historyIndex, maxHistory } = get();
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.stringify(elements));
    
    if (newHistory.length > maxHistory) {
      newHistory.shift();
    }
    
    set({
      history: newHistory,
      historyIndex: newHistory.length - 1,
    });
  },
  
  undo: () => {
    const { history, historyIndex } = get();
    if (historyIndex <= 0) return;
    
    const newIndex = historyIndex - 1;
    const elements = JSON.parse(history[newIndex]);
    
    set({
      elements,
      historyIndex: newIndex,
      selectedIds: [],
      isDirty: true,
    });
  },
  
  redo: () => {
    const { history, historyIndex } = get();
    if (historyIndex >= history.length - 1) return;
    
    const newIndex = historyIndex + 1;
    const elements = JSON.parse(history[newIndex]);
    
    set({
      elements,
      historyIndex: newIndex,
      selectedIds: [],
      isDirty: true,
    });
  },
  
  // ============ TOOL & STYLES ============
  
  setActiveTool: (tool) => set({ activeTool: tool }),
  
  setActiveStyles: (styles) => set(state => ({
    activeStyles: { ...state.activeStyles, ...styles }
  })),
  
  applyStylesToSelected: (styles) => {
    const { selectedIds } = get();
    if (selectedIds.length > 0) {
      get().updateElements(selectedIds, styles);
    }
    get().setActiveStyles(styles);
  },
  
  // ============ VIEW ============
  
  setZoom: (zoom) => set({ zoom: Math.max(0.1, Math.min(5, zoom)) }),
  
  zoomIn: () => set(state => ({ zoom: Math.min(5, state.zoom * 1.2) })),
  
  zoomOut: () => set(state => ({ zoom: Math.max(0.1, state.zoom / 1.2) })),
  
  resetZoom: () => set({ zoom: 1, panX: 0, panY: 0 }),
  
  setPan: (x, y) => set({ panX: x, panY: y }),
  
  toggleGrid: () => set(state => ({ showGrid: !state.showGrid })),
  
  toggleSnap: () => set(state => ({ snapToGrid: !state.snapToGrid })),
  
  setGridSize: (size) => set({ gridSize: size }),
  
  // ============ UI ============
  
  toggleDarkMode: () => set(state => ({ isDarkMode: !state.isDarkMode })),
  
  toggleIconLibrary: () => set(state => ({ showIconLibrary: !state.showIconLibrary })),
  
  toggleProperties: () => set(state => ({ showProperties: !state.showProperties })),
  
  toggleLayers: () => set(state => ({ showLayers: !state.showLayers })),
  
  // ============ DOCUMENT ============
  
  setDocumentName: (name) => set({ documentName: name, isDirty: true }),
  
  newDocument: () => {
    set({
      elements: [],
      selectedIds: [],
      history: [],
      historyIndex: -1,
      documentId: null,
      documentName: 'Sans titre',
      lastSaved: null,
      isDirty: false,
      zoom: 1,
      panX: 0,
      panY: 0,
    });
  },
  
  loadDocument: (doc) => {
    set({
      elements: doc.elements || [],
      documentId: doc.id,
      documentName: doc.name || 'Sans titre',
      lastSaved: doc.updated_at,
      isDirty: false,
      history: [JSON.stringify(doc.elements || [])],
      historyIndex: 0,
      selectedIds: [],
    });
  },
  
  markSaved: (docId, savedAt) => set({
    documentId: docId,
    lastSaved: savedAt,
    isDirty: false,
  }),
  
  // ============ COLLABORATION ============
  
  setSessionId: (id) => set({ sessionId: id }),
  
  setConnected: (connected) => set({ isConnected: connected }),
  
  updateCollaborators: (collaborators) => set({ collaborators }),
  
  updateCollaboratorCursor: (userId, cursor) => set(state => ({
    collaborators: state.collaborators.map(c =>
      c.userId === userId ? { ...c, cursor } : c
    )
  })),
  
  // Remote element updates (from other users)
  applyRemoteChanges: (changes) => {
    set(state => {
      let newElements = [...state.elements];
      
      changes.forEach(change => {
        switch (change.type) {
          case 'add':
            if (!newElements.find(el => el.id === change.element.id)) {
              newElements.push(change.element);
            }
            break;
          case 'update':
            newElements = newElements.map(el =>
              el.id === change.elementId ? { ...el, ...change.updates } : el
            );
            break;
          case 'delete':
            newElements = newElements.filter(el => el.id !== change.elementId);
            break;
        }
      });
      
      return { elements: newElements };
    });
  },
  
  // ============ EXPORT ============
  
  getExportData: () => {
    const { elements, documentName } = get();
    return {
      type: 'learspace-draw',
      version: 1,
      name: documentName,
      elements,
      exportedAt: new Date().toISOString(),
    };
  },
  
  importData: (data) => {
    if (data.type === 'learspace-draw' || data.type === 'excalidraw') {
      const elements = data.elements.map(el => ({
        ...el,
        id: el.id || uuidv4(),
      }));
      
      set({
        elements,
        documentName: data.name || 'Import',
        isDirty: true,
        history: [JSON.stringify(elements)],
        historyIndex: 0,
      });
    }
  },

  // Load elements from a template
  loadTemplateElements: (templateElements, templateName) => {
    // Generate new IDs for all elements to avoid conflicts
    const elements = templateElements.map(el => ({
      ...el,
      id: uuidv4(),
      createdAt: Date.now(),
      updatedAt: Date.now(),
    }));
    
    set({
      elements,
      documentName: templateName || 'Sans titre',
      isDirty: true,
      selectedIds: [],
      history: [JSON.stringify(elements)],
      historyIndex: 0,
      zoom: 1,
      panX: 0,
      panY: 0,
    });
  },
}));

export default useDrawStore;
